import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../Api";
import { useFlash } from "../context/flashcontext";
import { useAuth } from "../context/authcontext";

export function useProjectDetail(id) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { auth } = useAuth();
    const [project, setProject] = useState(null);
    const [editableProject, setEditableProject] = useState(null);
    const [profit, setProfit] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [availableEmployees, setAvailableEmployees] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [totalEmployeeCosts, setTotalEmployeeCosts] = useState(null);
    const [totalExtraMaterialCosts, setTotalExtraMaterialCosts] = useState(null);
    const [totalHours, setTotalHours] = useState(null);
    const [totalMeters, setTotalMeters] = useState(null);
    const { showFlash } = useFlash();

    const fetchProjectDetails = useCallback(async () => {
        if (!id) return;

        // Wait for auth to be available
        if (!auth || !auth.role || !auth.userId) {
            return;
        }

        setLoading(true);

        try {
            // First, check if ZZP/Voorman user has access
            if (auth.role === 'zzper' || auth.role === 'voorman') {
                // Fetch ONLY employees list first for access check
                const employeesRes = await api.get(`/projects/${id}/users`);
                const isAssigned = employeesRes.data.some(emp => emp.user_id === auth.userId);

                if (!isAssigned) {
                    setLoading(false);
                    showFlash?.(t('errors.noAccessToProject'), "error");
                    navigate('/projects', { replace: true });
                    return;
                }


                // User is assigned, now fetch the rest of the data
                const [projectRes, documentsRes, profitRes, allUsersRes] = await Promise.all([
                    api.get(`/projects/${id}`),
                    api.get(`/projects/${id}/documents`),
                    api.get(`/projects/${id}/profit`),
                    api.get(`/users`)
                ]);

                setProject(projectRes.data);
                setEditableProject(projectRes.data);
                setEmployees(employeesRes.data);
                setDocuments(documentsRes.data);

                // Extract all financial metrics from the profit response
                setProfit(profitRes.data.profit);
                setTotalEmployeeCosts(profitRes.data.total_employee_costs || 0);
                setTotalExtraMaterialCosts(profitRes.data.total_extra_material_costs || 0);
                setTotalHours(profitRes.data.total_hours || 0);
                setTotalMeters(profitRes.data.total_meters || 0);

                // Filter available employees - exclude customers and those already assigned
                const assignedUserIds = new Set(employeesRes.data.map(emp => emp.user_id));
                const available = allUsersRes.data.filter(user =>
                    !assignedUserIds.has(user.id) && user.role !== 'klant'
                );
                setAvailableEmployees(available);
            } else {
                // Owner/Finance can access all projects
                const [projectRes, employeesRes, documentsRes, profitRes, allUsersRes] = await Promise.all([
                    api.get(`/projects/${id}`),
                    api.get(`/projects/${id}/users`),
                    api.get(`/projects/${id}/documents`),
                    api.get(`/projects/${id}/profit`),
                    api.get(`/users`)
                ]);

                setProject(projectRes.data);
                setEditableProject(projectRes.data);
                setEmployees(employeesRes.data);
                setDocuments(documentsRes.data);

                // Extract all financial metrics from the profit response
                setProfit(profitRes.data.profit);
                setTotalEmployeeCosts(profitRes.data.total_employee_costs || 0);
                setTotalExtraMaterialCosts(profitRes.data.total_extra_material_costs || 0);
                setTotalHours(profitRes.data.total_hours || 0);
                setTotalMeters(profitRes.data.total_meters || 0);

                // Filter available employees - exclude customers and those already assigned to the project
                const assignedUserIds = new Set(employeesRes.data.map(emp => emp.user_id));
                const available = allUsersRes.data.filter(user =>
                    !assignedUserIds.has(user.id) && user.role !== 'klant'
                );
                setAvailableEmployees(available);
            }

            setError(null);
        } catch (err) {
            console.error("Error fetching project details:", err);

            // If unauthorized or forbidden, redirect
            if (err.response?.status === 401 || err.response?.status === 403) {
                setLoading(false);
                showFlash?.("Je hebt geen toegang tot dit project", "error");
                navigate('/projects', { replace: true });
                return;
            }

            setError("Fout bij ophalen projectdetails");
            showFlash?.("Fout bij ophalen projectdetails", "error");
        } finally {
            setLoading(false);
        }
    }, [id, showFlash, auth, navigate]);

    const fetchProjectProfit = useCallback(async () => {
        try {
            const response = await api.get(`/projects/${id}/profit`);
            setProfit(response.data.profit);
            setTotalEmployeeCosts(response.data.total_employee_costs || 0);
            setTotalExtraMaterialCosts(response.data.total_extra_material_costs || 0);
            setTotalHours(response.data.total_hours || 0);
            setTotalMeters(response.data.total_meters || 0);
        } catch (err) {
            // Optionally set error
        }
    }, [id]);

    const handleAddEmployee = async (selectedEmployee, selectedRole, rateType, hourlyRate, meterRate, fetchProjectDetails, showFlash, setShowAddEmployee, setSelectedEmployee, setSelectedRole, setHourlyRate, setMeterRate, setRateType) => {
        if(rateType === "hourly") {
            if (!selectedEmployee || !selectedRole || !hourlyRate || isNaN(hourlyRate) || hourlyRate <= 0) {
                showFlash("Vul alle velden correct in.", "error");
                return;
            }
        } else if (rateType === "meter") {
            if (!selectedEmployee || !selectedRole || !meterRate || isNaN(meterRate) || meterRate <= 0) {
                showFlash("Vul alle velden correct in.", "error");
                return;
            }
        }
        try {
            await api.post(`/projects/${id}/users`, {
                user_id: parseInt(selectedEmployee),
                projectrole: selectedRole,
                hourly_rate: rateType === "hourly" ? parseFloat(hourlyRate) : 0,
                meter_rate: rateType === "meter" ? parseFloat(meterRate) : 0,
                rate_type: rateType,
            });
            await fetchProjectDetails();
            setShowAddEmployee(false);
            setSelectedEmployee("");
            setSelectedRole("");
            setHourlyRate("");
            setMeterRate("");
            setRateType("hourly");
            showFlash(t('success.employeeAdded'), "success");
        } catch (err) {
            console.error("Error adding employee:", err);
            showFlash(t('errors.addEmployeeFailed'), "error");
        }
    };

    const handleEditEmployee = (employee, setEditingEmployeeId, setNewProjectRole, setNewRateType, setNewHourlyRate, setNewMeterRate) => {
        setEditingEmployeeId(employee.user_id);
        setNewProjectRole(employee.project_role);
        setNewRateType(employee.rate_type);
        setNewHourlyRate(employee.hourly_rate);
        setNewMeterRate(employee.meter_rate);
    };

    const handleSaveEmployeeChanges = async (employeeId, newRateType, newMeterRate, newHourlyRate, newProjectRole, setEditingEmployeeId) => {
        try {
            await api.put(`/projects/${id}/rates`, {
                user_id: employeeId,
                rate_type: newRateType, // Always include rate_type
                meter_rate: newRateType === "meter" ? parseFloat(newMeterRate) : 0,
                hourly_rate: newRateType === "hourly" ? parseFloat(newHourlyRate) : 0,
            });
            await api.put(`/projects/${id}/projectrole/${employeeId}`, {
                user_id: employeeId,
                project_role: newProjectRole, // use project_role as key
            });
            await fetchProjectDetails();
            showFlash(t('success.employeeUpdated'), "success");
            setEditingEmployeeId(null);
        } catch (err) {
            console.error("Error updating employee:", err);
            showFlash(t('errors.updateEmployeeFailed'), "error");
        }
    };

    // --- Remove employee with modal confirmation ---
    const [showRemoveEmployeeModal, setShowRemoveEmployeeModal] = useState(false);
    const [employeeToRemove, setEmployeeToRemove] = useState(null);

    const requestRemoveEmployee = (employeeId) => {
        setEmployeeToRemove(employeeId);
        setShowRemoveEmployeeModal(true);
    };

    const confirmRemoveEmployee = async () => {
        if (!employeeToRemove) return;
        try {
            await api.delete(`/projects/${id}/users/${employeeToRemove}`);
            showFlash(t('success.employeeRemoved'), "success");
            await fetchProjectDetails();
        } catch (err) {
            // Try to show backend error message if available
            let msg = t('errors.removeEmployeeFailed');
            if (err && err.response && err.response.data && err.response.data.error) {
                msg = err.response.data.error;
            } else if (err && err.message) {
                msg = err.response.data || err.message;
            }
            msg = msg.charAt(0).toUpperCase() + msg.slice(1);
            showFlash(msg, "error");
        } finally {
            setShowRemoveEmployeeModal(false);
            setEmployeeToRemove(null);
        }
    };

    const cancelRemoveEmployee = () => {
        setShowRemoveEmployeeModal(false);
        setEmployeeToRemove(null);
    };

    // --- Upload document ---
    const handleDocumentUpload = async (e, file, documentCategory, expiresAt, fetchProjectDetails, setFile, setDocumentCategory, setExpiresAt, setShowUploadForm) => {
        e.preventDefault();
        if (!file || !documentCategory) {
            showFlash(t('errors.selectFileAndType'), "error");
            return;
        }
        const formData = new FormData();
        formData.append("document", file);
        formData.append("document_category", documentCategory);
        if (expiresAt) {
            // Convert to RFC3339 format (YYYY-MM-DDT00:00:00Z)
            const rfc3339 = new Date(expiresAt).toISOString();
            formData.append("expires_at", rfc3339);
        }
        try {
            await api.post(`/projects/${id}/documents/upload`, formData);
            showFlash(t('success.projectSaved'), "success");
            await fetchProjectDetails();
        } catch (err) {
            showFlash(t('errors.uploadDocumentFailed'), "error");
        }
        setFile(null);
        setDocumentCategory("");
        setExpiresAt("");
        setShowUploadForm(false);
    };

    // --- Remove project document with modal confirmation ---
    const [showRemoveDocumentModal, setShowRemoveDocumentModal] = useState(false);
    const [documentToRemove, setDocumentToRemove] = useState(null);
    const [editingDocId, setEditingDocId] = useState(null);
    const [editedExpiry, setEditedExpiry] = useState("");

    const requestRemoveDocument = (docId) => {
        setDocumentToRemove(docId);
        setShowRemoveDocumentModal(true);
    };

    const confirmRemoveDocument = async (fetchProjectDetails, showFlash) => {
        if (!documentToRemove) return;
        try {
            await api.delete(`/projects/${id}/documents/${documentToRemove}`);
            showFlash(t('success.documentDeleted'), "success");
            await fetchProjectDetails();
        } catch (err) {
            showFlash(t('errors.removeDocumentFailed'), "error");
        } finally {
            setShowRemoveDocumentModal(false);
            setDocumentToRemove(null);
        }
    };

    const cancelRemoveDocument = () => {
        setShowRemoveDocumentModal(false);
        setDocumentToRemove(null);
    };

    // --- Edit document expiry date ---
    const handleEditExpiry = (doc) => {
        setEditingDocId(doc.id);
        setEditedExpiry(doc.expires_at ? new Date(doc.expires_at).toISOString().split('T')[0] : "");
    };

    const handleSaveExpiry = async (docId) => {
        try {
            await api.put(`/projects/${id}/documents/${docId}/expiry`, {
                expires_at: editedExpiry ? new Date(editedExpiry).toISOString() : null
            });
            setEditingDocId(null);
            setEditedExpiry("");
            showFlash?.(t('success.expiryUpdated'), "success");
            await fetchProjectDetails();
        } catch (err) {
            showFlash?.(t('errors.saveFailed'), "error");
        }
    };

    const handleCancelEditExpiry = () => {
        setEditingDocId(null);
        setEditedExpiry("");
    };

    // --- Save edited project ---
    const handleSaveClick = async () => {
        if (!editableProject) return;
        try {
            // Only send the fields that the backend accepts
            const projectUpdate = {
                name: editableProject.name,
                location: editableProject.location,
                address: editableProject.address,
                client: editableProject.client,
                projectprice: editableProject.projectprice,
                projectrate: editableProject.projectrate,
                materialcosts: editableProject.materialcosts,
                whatsapp: editableProject.whatsapp,
                start_date: editableProject.start_date ? new Date(editableProject.start_date).toISOString() : null,
                end_date: editableProject.end_date ? new Date(editableProject.end_date).toISOString() : null,
            };

            await api.put(`/projects/${id}`, projectUpdate);
            await fetchProjectDetails();
            setIsEditing(false);
            showFlash(t('success.projectUpdated'), "success");
        } catch (err) {
            console.error("Error saving project:", err);
            showFlash(t('errors.saveFailed'), "error");
        }
    };

    // --- Delete a document from the project ---
    const handleDeleteDocument = async (docId) => {
        try {
            await api.delete(`/projects/${id}/documents/${docId}`);
            await fetchProjectDetails();
            showFlash(t('success.documentDeleted'), "success");
        } catch (err) {
            showFlash(t('errors.removeDocumentFailed'), "error");
        }
    };

    // --- Fetch project assignments ---
    const fetchAssignments = useCallback(async () => {
        try {
            const response = await api.get(`/projects/${id}/assignments`);
            setAssignments(response.data || []);
        } catch (err) {
            console.error("Error fetching assignments:", err);
            // Don't show error, assignments are optional
        }
    }, [id]);

    // --- Create assignment ---
    const handleCreateAssignment = async (assignmentData) => {
        try {
            await api.post('/project-user-assignments', assignmentData);
            await fetchAssignments();
            showFlash(t('success.assignmentAdded'), "success");
            return true;
        } catch (err) {
            console.error("Error creating assignment:", err);
            showFlash(t('errors.createAssignment'), "error");
            return false;
        }
    };

    // --- Update assignment ---
    const handleUpdateAssignment = async (assignmentId, updates) => {
        try {
            await api.put(`/project-user-assignments/${assignmentId}`, updates);
            await fetchAssignments();
            showFlash(t('success.assignmentAdded'), "success");
            return true;
        } catch (err) {
            console.error("Error updating assignment:", err);
            showFlash(t('errors.updateAssignment'), "error");
            showFlash("Fout bij bijwerken toewijzing", "error");
            return false;
        }
    };

    // --- Delete assignment ---
    const handleDeleteAssignment = async (assignmentId) => {
        try {
            await api.delete(`/project-user-assignments/${assignmentId}`);
            await fetchAssignments();
            showFlash("Toewijzing verwijderd!", "success");
        } catch (err) {
            console.error("Error deleting assignment:", err);
            showFlash("Fout bij verwijderen toewijzing", "error");
        }
    };

    useEffect(() => {
        // Only fetch if auth is available
        if (auth && auth.role && auth.userId) {
            fetchProjectDetails();
            fetchProjectProfit();
            fetchAssignments();
        }
    }, [fetchProjectDetails, fetchProjectProfit, fetchAssignments, auth]);

    return {
        project,
        setProject,
        editableProject,
        setEditableProject,
        isEditing,
        setIsEditing,
        profit,
        employees,
        availableEmployees,
        documents,
        assignments,
        totalEmployeeCosts,
        totalExtraMaterialCosts,
        totalHours,
        totalMeters,
        loading,
        error,
        fetchProjectDetails,
        fetchProjectProfit,
        fetchAssignments,
        handleAddEmployee,
        handleEditEmployee,
        handleSaveEmployeeChanges,
        handleSaveClick,
        handleCreateAssignment,
        handleUpdateAssignment,
        handleDeleteAssignment,
        requestRemoveEmployee,
        confirmRemoveEmployee,
        cancelRemoveEmployee,
        showRemoveEmployeeModal,
        employeeToRemove,
        requestRemoveDocument,
        confirmRemoveDocument,
        cancelRemoveDocument,
        showRemoveDocumentModal,
        documentToRemove,
        handleDocumentUpload,
        showFlash,
        handleDeleteDocument,
        editingDocId,
        editedExpiry,
        setEditedExpiry,
        handleEditExpiry,
        handleSaveExpiry,
        handleCancelEditExpiry
    };
}
